import React, { useEffect, useRef, useState } from "react";

/**
 * "Why GigAfloat Exists" — premium homepage comparison section.
 * 4 gig-platform cards + 1 featured GigAfloat card (highest visual priority).
 *
 * Brand palette only:
 *   Matte Black   #050505
 *   Carbon        #0A0A0A
 *   Neon Emerald  #00FF88
 *   Mint Glow     #14F195
 *   White         #FFFFFF
 * Fonts: Unbounded (headings, `font-display`) · Manrope (body) ·
 *        JetBrains Mono (metric labels, `font-mono-c`)
 */
const PLATFORMS = [
  {
    emoji: "🚗",
    name: "DoorDash",
    body:
      "A driver who works a 10 hour shift may still need to end their Dash to access those earnings. If they continue working throughout the day, the money earned from earlier deliveries may not be immediately available.",
  },
  {
    emoji: "🚘",
    name: "Uber",
    body:
      "A driver can complete rides all day long, but tips and portions of their earnings may not be available right away. The work is complete, but access to those funds can still require waiting.",
  },
  {
    emoji: "🛒",
    name: "Instacart",
    body:
      "A shopper may finish multiple batches throughout the day, but tip adjustments and processing windows can delay access to a portion of those earnings.",
  },
  {
    emoji: "⚡",
    name: "Spark",
    body:
      "A driver may complete deliveries throughout the day, but earnings are often subject to payout timing and processing windows before becoming available.",
  },
];

/* IntersectionObserver-driven fade-in. */
const useReveal = () => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, seen];
};

const PlatformCard = ({ emoji, name, body, index }) => {
  const [ref, seen] = useReveal();
  return (
    <article
      ref={ref}
      data-testid={`why-card-${name.toLowerCase()}`}
      className={`relative rounded-3xl p-7 lg:p-8 bg-[#0A0A0A] border border-white/[0.06] transition-all duration-700 ease-out group hover:border-[#00FF88]/30 hover:-translate-y-1`}
      style={{
        backdropFilter: "blur(20px) saturate(140%)",
        opacity: seen ? 1 : 0,
        transform: seen ? "translateY(0)" : "translateY(28px)",
        transitionDelay: `${index * 90}ms`,
      }}
    >
      {/* subtle hover sheen */}
      <div
        className="pointer-events-none absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background:
            "radial-gradient(circle at 50% 0%, rgba(0,255,136,0.10), transparent 60%)",
        }}
      />
      <div className="relative">
        <div className="text-4xl mb-4 leading-none">{emoji}</div>
        <h3 className="font-display font-extrabold text-2xl text-white tracking-tight mb-4">
          {name}
        </h3>
        <p className="text-white/60 text-[14.5px] leading-relaxed mb-6 min-h-[120px]">
          {body}
        </p>

        <div className="space-y-3 pt-5 border-t border-white/[0.06]">
          <div>
            <div className="font-mono-c text-[10px] uppercase tracking-[0.22em] text-white/40 mb-1">
              Earned
            </div>
            <div className="text-white font-semibold flex items-center gap-2">
              <span aria-hidden="true">✅</span> Today
            </div>
          </div>
          <div>
            <div className="font-mono-c text-[10px] uppercase tracking-[0.22em] text-white/40 mb-1">
              Available
            </div>
            <div className="text-white/75 font-medium flex items-center gap-2">
              <span aria-hidden="true">⏳</span> Not always immediately
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

const GigAfloatCard = ({ index }) => {
  const [ref, seen] = useReveal();
  return (
    <article
      ref={ref}
      data-testid="why-card-gigafloat"
      className="relative rounded-3xl p-8 lg:p-10 transition-all duration-700 ease-out gf-feature-card group"
      style={{
        background:
          "linear-gradient(155deg, rgba(0,255,136,0.08) 0%, rgba(20,241,149,0.04) 60%, rgba(5,5,5,1) 100%)",
        border: "1.5px solid rgba(0,255,136,0.55)",
        opacity: seen ? 1 : 0,
        transform: seen ? "translateY(0)" : "translateY(28px)",
        transitionDelay: `${index * 90}ms`,
      }}
    >
      {/* halo behind card */}
      <div
        className="pointer-events-none absolute -inset-2 rounded-[28px] -z-10 gf-feature-halo"
        style={{
          background:
            "radial-gradient(60% 60% at 50% 40%, rgba(0,255,136,0.35), transparent 70%)",
          filter: "blur(28px)",
        }}
      />
      <div className="relative">
        <div className="flex items-start justify-between mb-5">
          <div className="text-4xl leading-none">💚</div>
          <div className="font-mono-c text-[9px] tracking-[0.3em] uppercase text-[#00FF88] font-bold flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00FF88] pulse-dot" />
            Built for this
          </div>
        </div>

        <h3 className="font-display font-black text-3xl lg:text-[34px] text-white tracking-tight mb-5">
          GigAfloat
        </h3>

        <div className="space-y-4">
          {[
            { label: "Work completed.", val: "Earnings already earned", icon: "✅" },
            { label: "Earnings verified.", val: "Eligibility based on real earnings", icon: "✅" },
            { label: "Float sent upon approval.", val: "Access before the next payout", icon: "🚀" },
          ].map((row) => (
            <div key={row.label} className="rounded-2xl bg-[#050505]/60 border border-[#00FF88]/15 p-4">
              <div className="text-white font-semibold text-[15px] mb-1">{row.label}</div>
              <div className="flex items-center gap-2 text-[#00FF88] font-medium text-sm">
                <span aria-hidden="true">{row.icon}</span>
                <span>{row.val}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </article>
  );
};

const HighlightStatement = () => {
  const [ref, seen] = useReveal();
  return (
    <div
      ref={ref}
      data-testid="why-highlight"
      className="relative mt-20 lg:mt-28 rounded-3xl overflow-hidden border border-[#00FF88]/20 bg-[#0A0A0A] p-10 lg:p-16 text-center"
      style={{
        opacity: seen ? 1 : 0,
        transform: seen ? "translateY(0)" : "translateY(20px)",
        transition: "all 800ms ease-out",
      }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 50% 0%, rgba(0,255,136,0.18), transparent 60%)",
        }}
      />
      <div className="relative">
        <h3 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white">
          The money was earned.
        </h3>
        <h4 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white/55 tracking-tight">
          The payout just has not arrived yet.
        </h4>
        <p className="mt-6 max-w-2xl mx-auto text-white/65 text-[15.5px] leading-relaxed">
          GigAfloat is building a better way to bridge the gap between completed
          work and platform payouts.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5 font-mono-c text-[11px] uppercase tracking-[0.22em]">
          <span className="text-[#00FF88]">Powered by real earnings.</span>
          <span className="hidden sm:inline-block w-1 h-1 rounded-full bg-white/30" />
          <span className="text-white/55">Built for gig workers.</span>
        </div>
      </div>
    </div>
  );
};

const WhyGigAfloat = () => {
  return (
    <section
      id="why"
      data-testid="why-gigafloat-section"
      className="relative py-24 md:py-32 border-t border-white/5 bg-[#050505]"
    >
      {/* ambient backdrop */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 70% 50% at 50% 10%, rgba(0,255,136,0.06), transparent 60%)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="max-w-3xl mb-14 md:mb-16">
          <div className="font-mono-c text-[10px] uppercase tracking-[0.25em] text-[#00FF88] mb-4">
            // Why GigAfloat Exists
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.02]">
            Why GigAfloat Exists
          </h2>
          <h3 className="mt-5 font-display text-2xl sm:text-3xl font-bold text-white/55 tracking-tight leading-snug">
            The work is done. The payout is still pending.
          </h3>
          <p className="mt-5 text-white/60 text-base lg:text-lg leading-relaxed max-w-2xl">
            Millions of gig workers complete jobs every day, yet access to their
            earnings is often delayed by platform payout schedules, processing
            times, and tip settlement windows.
          </p>
        </div>

        {/* Card grid — 4 platform cards + 1 featured card.
            Desktop: 5 columns equal, featured card spans wider visual via internal scale.
            Tablet: 2 columns. Mobile: 1 column, featured pinned first. */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-5 lg:gap-6 items-stretch">
          {PLATFORMS.map((p, i) => (
            <PlatformCard key={p.name} {...p} index={i} />
          ))}
          {/* Featured GigAfloat — last on desktop so eye trails to the answer,
              first on mobile so the value prop hits first. */}
          <div className="xl:scale-[1.02] xl:z-10 order-first xl:order-none">
            <GigAfloatCard index={PLATFORMS.length} />
          </div>
        </div>

        <HighlightStatement />
      </div>
    </section>
  );
};

export default WhyGigAfloat;
